import type { Dispatch, FC, SetStateAction } from 'react'

import { NPS_RATED, NPS_UNRATED } from '@oneToOne/shared/testIds'

import { UnstyledButton } from '@core/components/twind/Button'
import { useContextTranslation } from '@core/i18n'
import { useTwindContext } from '@core/twind'

import { ratingButton, ratingTips } from './Rating.twind'

interface RatingProps {
  currentRating: number | undefined
  onChange: Dispatch<SetStateAction<number>>
}

/**
 * Helper component for rendering a rating component.
 */
export const Rating: FC<RatingProps> = ({ currentRating, onChange }) => {
  const { tw } = useTwindContext()
  const { t } = useContextTranslation()

  return (
    <div>
      <div className={tw?.('flex', 'justify-center', 'items-center', 'gap-2')}>
        {[1, 2, 3, 4, 5].map((rating) => {
          const isRated = rating === Number(currentRating)
          return (
            <UnstyledButton
              className={tw?.(ratingButton({ rating, isRated }))}
              tabIndex={0}
              key={rating}
              data-testid={isRated ? NPS_RATED : NPS_UNRATED}
              onClick={() => onChange(rating)}
              aria-label={t('Rate {{rating}} out of 5', {
                rating,
              })}
            >
              {rating}
            </UnstyledButton>
          )
        })}
      </div>
      <div
        className={tw?.(
          'flex',
          'justify-between',
          'items-center',
          'mt-1',
          'w-full',
        )}
      >
        <span className={tw?.(ratingTips(), 'text-sm')}>
          {t('Dissatisfied')}
        </span>
        <span className={tw?.(ratingTips({ satisfied: true }), 'text-sm')}>
          {t('Satisfied')}
        </span>
      </div>
    </div>
  )
}
